/**
 * Identifiers.
 *
 * The old codebase had six id schemes. Three had measured collision rates: a float
 * `ts + Math.random()` that collided about 1 in 6,250 for same-millisecond food
 * logs, a `"bw" + Date.now()` with no random component at all, and a `generateId`
 * whose comment claimed ObjectId compatibility while producing 19 hex characters,
 * so `new ObjectId(id)` threw on every value it made and two server routes wrapped
 * it in try/catch to cope.
 *
 * There are two kinds of identifier here and no others:
 *
 *   Record id      Minted by the server, an ObjectId, exposed to the client as a
 *                  24 character hex string. The client never invents one.
 *   Idempotency key Minted by the client, a UUID v4, sent with a create so a retry
 *                  cannot double-write. Unique-indexed per owner, so a duplicate
 *                  fails loudly instead of silently overwriting.
 *
 * Audit references prevented here:
 *   utils.js #1, #2  (invalid ObjectIds, client-minted primary keys)
 *   nutrition.js #1  (colliding float ids)
 *   health.jsx #9    (a scheme with no randomness)
 *   food.jsx #6      (three more schemes in one file)
 *   store.js #18     (client-minted ids accepted as the record key)
 */

/** A record id, as the client sees it: 24 lowercase hex characters. */
export const RECORD_ID_PATTERN = /^[0-9a-f]{24}$/;

/** UUID v4, the only idempotency key shape accepted. */
export const IDEMPOTENCY_KEY_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Mint an idempotency key for a create request.
 *
 * `crypto.randomUUID` is cryptographically random and available in every runtime
 * this product targets, so there is nothing to hand-roll and no dependency to add.
 *
 * @returns {string} A UUID v4.
 * @throws {Error} if the runtime has no Web Crypto, which would mean an
 *   environment this product does not support.
 */
export function newIdempotencyKey() {
  if (typeof globalThis.crypto?.randomUUID !== 'function') {
    throw new Error('crypto.randomUUID is unavailable, so a safe idempotency key cannot be minted');
  }
  return globalThis.crypto.randomUUID();
}

/**
 * Whether a value is a well-formed record id.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isRecordId(value) {
  return typeof value === 'string' && RECORD_ID_PATTERN.test(value);
}

/**
 * Whether a value is a well-formed idempotency key.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isIdempotencyKey(value) {
  return typeof value === 'string' && IDEMPOTENCY_KEY_PATTERN.test(value);
}

/**
 * Assert a record id at a trust boundary.
 *
 * @param {unknown} value
 * @returns {string}
 * @throws {TypeError} rather than letting a malformed id reach a query.
 */
export function assertRecordId(value) {
  if (!isRecordId(value)) {
    throw new TypeError(`Expected a 24 character hex record id, received ${JSON.stringify(value)}`);
  }
  return value;
}
